import React, { useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Dropdown } from 'react-bootstrap';
import { logout } from '../../features/auth/authSlice';
import {
  formatEnvelopeRemaining,
  shouldShowSubscriptionUsage,
} from '../../utils/subscription';
import NotificationsDropdown from './NotificationsDropdown';

const Header = ({ onMenuClick }) => {
  const { user } = useSelector((state) => state.auth);
  const userSubscription = useSelector((state) => state.subscription?.userSubscription);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const canManageOrgUsers = Boolean(
    user?.is_organization_admin ?? user?.is_organization_owner
  );

  const showUsage = shouldShowSubscriptionUsage(userSubscription);

  const remainingLabel = useMemo(
    () => (showUsage
      ? formatEnvelopeRemaining(userSubscription?.usage, userSubscription)
      : null),
    [showUsage, userSubscription]
  );

  const remainingLabelShort = useMemo(() => {
    if (!showUsage) return null;
    const entry = userSubscription?.usage?.envelopes;
    if (!entry) return null;
    const { used, limit, remaining } = entry;
    if (limit == null) return 'Unlimited';
    const left =
      remaining != null && Number.isFinite(remaining)
        ? Math.max(0, remaining)
        : Math.max(0, limit - used);
    return `${left} left`;
  }, [showUsage, userSubscription?.usage]);

  const handleLogout = async () => {
    try {
      await dispatch(logout()).unwrap();
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (user?.name) {
      return user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return user?.email?.slice(0, 2).toUpperCase() || 'U';
  };

  return (
    <header className="dashboard-header">
      <div className="header-content">
        <button
          type="button"
          className="header-menu-btn"
          onClick={onMenuClick}
          aria-label="Open navigation menu"
        >
          <i className="ri-menu-line"></i>
        </button>

        {/* Search Bar — full-width second row on mobile */}
        <div className="header-search">
          <div className="search-input-wrapper">
            <i className="ri-search-line search-icon"></i>
            <input
              type="text"
              className="form-control search-input"
              placeholder="Search documents"
            />
          </div>
        </div>

        {/* Right Side Actions */}
        <div className="header-actions">
          {remainingLabel && (
            <>
              <span className="header-usage-remaining header-usage-remaining--full text-muted small">
                {remainingLabel}
              </span>
              <span className="header-usage-remaining header-usage-remaining--short text-muted small">
                {remainingLabelShort}
              </span>
            </>
          )}
          {canManageOrgUsers && (
            <Button
              as={Link}
              to="/plans"
              variant="warning"
              size="sm"
              className="upgrade-btn"
              title="Plans and pricing"
            >
              Upgrade
            </Button>
          )}

          <div className="header-icons">
            <NotificationsDropdown />
            <button className="icon-btn header-icon-desktop" title="Messages">
              <i className="ri-mail-line"></i>
            </button>
            <button className="icon-btn header-icon-desktop" title="Help">
              <i className="ri-question-line"></i>
            </button>
            <Dropdown align="end">
              <Dropdown.Toggle
                as="div"
                className="user-avatar"
                id="user-dropdown"
                style={{ cursor: 'pointer' }}
              >
                <span className="avatar-text">{getUserInitials()}</span>
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.ItemText>
                  <div className="d-flex flex-column">
                    <strong>{user?.name || 'User'}</strong>
                    <small className="text-muted">{user?.email}</small>
                  </div>
                </Dropdown.ItemText>
                <Dropdown.Divider />
                {canManageOrgUsers && (
                  <Dropdown.Item onClick={() => navigate('/settings/users')}>
                    <i className="ri-team-line me-2" />
                    User management
                  </Dropdown.Item>
                )}
                {canManageOrgUsers && (
                  <Dropdown.Item onClick={() => navigate('/settings/billing')}>
                    <i className="ri-bank-card-line me-2" />
                    Billing
                  </Dropdown.Item>
                )}
                <Dropdown.Item onClick={handleLogout}>
                  <i className="ri-logout-box-line me-2"></i>
                  Logout
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
